import React, { useEffect, useState } from "react";
import { Button, Col, Dropdown, Modal, Table, Tag, Typography } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import { openNotificationWithIcon } from "helpers/OpenNotificationWithIcon";
import { useQueryClient } from "react-query";
import endPoints from "../EndPoints";
import { ItemType } from "antd/es/menu/interface";
import { MazakaBull } from "apps/Common/MazakaBull";
import { useNavigate } from "react-router-dom";
import { deleteSavedSearch } from "../Services";
import { useGetSavedSearchs } from "../ServerSideStates";
import { useDispatch } from "react-redux";
import { handleSetAdvertListFilter } from "apps/RealState/ListItems/ClientStates";
import SaveFilterData from "apps/RealState/ListItems/Components/SaveFilterData";
import useWindowSize from "helpers/useWindowSize";

const { Text } = Typography;

const ListItems = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [isShowEditModal, setIsShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const windowSize = useWindowSize();
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    setIsSmallScreen(windowSize.width < 768);
  }, [windowSize.width]);

  const columns = [
    {
      title: "Arama Adı",
      dataIndex: "name",
      render: (value: string, record: any) => {
        return (
          <div className="!flex flex-col gap-4 w-full px-4">
            <Text className="font-semibold text-base w-full break-words">{value}</Text>
            <div className="flex flex-wrap gap-2 w-full">
              {record?.notificationTypeSavedSearch?.map((item: any) => (
                <Tag
                  key={item.notificationTypeId}
                  color={item.notificationTypeId === 1 ? "blue" : "green"}
                >
                  {item.notificationTypeId === 1 ? "E-Posta" : "Mobil"}
                </Tag>
              ))}
            </div>
            <div className="flex items-center gap-2 w-full">
              <MazakaBull type="Success" />
              <Text
                onClick={async () => {
                  const filter = JSON.parse(record?.searchCriteria);
                  await dispatch(handleSetAdvertListFilter({ filter }));
                  navigate("/ilan");
                }}
                className="text-xs text-blue-500 cursor-pointer"
              >
                Filtreyi Uygula
              </Text>
            </div>
            <div className="flex gap-3 w-full">
              <Button
                icon={<DeleteOutlined />}
                onClick={() => confirm(record)}
                type="text"
                className="text-red-500 p-0 text-xs"
              >
                Sil
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={async () => {
                  setSelectedRecord(record);
                  setIsShowEditModal(true);
                }}
                type="text"
                className="text-blue-500 p-0 text-xs"
              >
                Güncelle
              </Button>
            </div>
          </div>
        );
      },
    },
    ...(!isSmallScreen
      ? [
          {
            title: "Bildirim Yolları",
            dataIndex: "notificationTypeSavedSearch",
            render: (types: any[]) => (
              <div className="flex flex-wrap gap-2">
                {types.map((item: any) => (
                  <Tag
                    key={item.notificationTypeId}
                    color={item.notificationTypeId === 1 ? "blue" : "green"}
                  >
                    {item.notificationTypeId === 1 ? "E-Posta" : "Mobil"}
                  </Tag>
                ))}
              </div>
            ),
          },
          {
            title: "Filtre",
            render: (_: any, record: any) => (
              <div className="flex items-center gap-2">
                <MazakaBull type="Success" />
                <Text
                  onClick={async () => {
                    const filter = JSON.parse(record?.searchCriteria);
                    await dispatch(handleSetAdvertListFilter({ filter }));
                    navigate("/ilan");
                  }}
                  className="text-xs text-blue-500 cursor-pointer"
                >
                  Filtreyi Uygula
                </Text>
              </div>
            ),
          },
          {
            title: "",
            dataIndex: "edit",
            key: "edit",
            width: "8%",
            render: (_: any, record: any) => (
              <Col xs={24} className="text-end pr-2">
                <Dropdown menu={{ items: tableItemDropdownMenu(record) }}>
                  <EllipsisOutlined />
                </Dropdown>
              </Col>
            ),
          },
        ]
      : []),
  ];

  const tableItemDropdownMenu = (record: any): ItemType[] => [
    {
      key: "1",
      onClick: async () => {
        setSelectedRecord(record);
        setIsShowEditModal(true);
      },
      icon: <EditOutlined />,
      label: "Güncelle",
    },
    {
      key: "2",
      onClick: () => confirm(record),
      icon: <DeleteOutlined />,
      label: "Sil",
    },
  ];

  const confirm = (record: any) => {
    Modal.confirm({
      title: "Uyarı",
      icon: <ExclamationCircleOutlined />,
      content: `Bu öğe silinecek. Onaylıyor musunuz?`,
      okText: "Sil",
      cancelText: "Vazgeç",
      onOk: async () => {
        try {
          await deleteSavedSearch(record);
          openNotificationWithIcon("default", "İşlem Başarılı");
          queryClient.resetQueries({
            queryKey: endPoints.getSavedSearchs,
            exact: false,
          });
        } catch (error: any) {
          openNotificationWithIcon("default", error?.message);
        }
      },
    });
  };

  const stores = useGetSavedSearchs();

  return (
    <>
      <Table
        columns={columns}
        dataSource={stores?.data?.data || []}
        loading={stores.isLoading || stores.isFetching}
        rowKey="id"
        pagination={{
          position: ["bottomRight"],
          className: "px-0",
          total: stores?.data?.data?.length || 0,
          pageSize: 20,
          showLessItems: true,
          size: "small",
          showSizeChanger: true,
          locale: { items_per_page: "" },
          showTotal: (e) => `${e}`,
        }}
      />

      <Modal
        open={isShowEditModal}
        onCancel={() => setIsShowEditModal(false)}
        title={false}
        footer={false}
      >
        <SaveFilterData
          selectedRecord={selectedRecord}
          onFinish={() => setIsShowEditModal(false)}
        />
      </Modal>
    </>
  );
};

export default ListItems;
